import { i18n } from '@marine/i18n';
import type { ApiClient, Endpoints } from '../rest-api/generated/laravel-typed-openapi';
import {
  ORG_ENTITLEMENT,
  type OrgEntitlement,
  type OrgLimits,
  type OrgPlan,
  PLAN_SLUG,
  PLANS_IN_ORDER,
  type PlanSlug,
  planAllowance,
} from './orgPlan';

/**
 * The backend's plan routes, read into the app's terms: `GET /organization/plan` (MDC-4529) and
 * `GET /plans` (MDC-4530).
 *
 * The backend answers with what it enforces, so its limits replace the MDC-4517 table in `orgPlan`
 * wherever it serves them. The table stays for what the backend doesn't serve (TheBoatDB compare and
 * API, priority support, commercial use) and for when the routes can't be reached: offline, or
 * before the first answer.
 */

type OrganizationPlanData = Endpoints.get_Plan_organizationPlan['responses'][200]['data'];
type PlanListItem = Endpoints.get_Plan_plans['responses'][200]['data'][number];

export type ServerLimits = OrganizationPlanData['limits'];
export type ServerEntitlements = OrganizationPlanData['entitlements'];

const MB_PER_GB = 1024;

/** The entitlements the backend decides. The rest of `ORG_ENTITLEMENT` keeps the table's answer. */
const SERVED_ENTITLEMENTS: Readonly<Record<OrgEntitlement, keyof ServerEntitlements | null>> = {
  [ORG_ENTITLEMENT.customLocations]: 'custom_locations',
  [ORG_ENTITLEMENT.customCategories]: 'custom_categories',
  [ORG_ENTITLEMENT.customFields]: 'custom_fields',
  [ORG_ENTITLEMENT.theBoatDbApi]: null,
  [ORG_ENTITLEMENT.prioritySupport]: null,
  [ORG_ENTITLEMENT.commercialUse]: null,
};

type Allowance = { limits: OrgLimits; entitlements: ReadonlySet<OrgEntitlement> };

/**
 * A plan's allowance as the backend states it, over `fallback` for what it doesn't state.
 *
 * Two of its numbers need reading. Members of 0 is unlimited, as in Clerk, which the backend keeps
 * in step (MDC-4501). Boats of 0 is an account the backend found no plan subscription for, where
 * adding a boat still checks against Free's one boat, so the fallback stands.
 */
export const allowanceFromServer = (
  limits: ServerLimits,
  entitlements: ServerEntitlements,
  fallback: Allowance,
): Allowance => ({
  limits: {
    boats: limits.boats > 0 ? limits.boats : fallback.limits.boats,
    users: limits.members > 0 ? limits.members : null,
    storageGb: limits.storage_mb / MB_PER_GB,
    aiQueriesPerMonth: limits.ai_messages,
    theBoatDbCompares: fallback.limits.theBoatDbCompares,
  },
  entitlements: new Set(
    Object.values(ORG_ENTITLEMENT).filter((entitlement) => {
      const served = SERVED_ENTITLEMENTS[entitlement];

      return served === null ? fallback.entitlements.has(entitlement) : entitlements[served];
    }),
  ),
});

/** What `GET /organization/plan` adds to the synced plan. */
export type ServerOrgPlan = {
  limits: ServerLimits;
  entitlements: ServerEntitlements;
  usage: {
    storageGb: number;
    aiQueries: number;
    /** When this month's AI queries start again from zero. */
    aiQueriesResetAt: string | null;
  };
};

export const fetchServerOrgPlan = async (apiClient: ApiClient): Promise<ServerOrgPlan> => {
  const { data } = await apiClient.get('/organization/plan');

  return {
    limits: data.limits,
    entitlements: data.entitlements,
    usage: {
      storageGb: data.usage.storage_mb / MB_PER_GB,
      aiQueries: data.usage.ai_messages,
      aiQueriesResetAt: data.usage.ai_messages_reset_at || null,
    },
  };
};

/**
 * The synced plan with the backend's limits and entitlements.
 *
 * Only those: the plan itself, its status and its dates stay the synced row's, which every member
 * has offline and which updates the moment Stripe's webhook lands.
 */
export const withServerAllowance = (plan: OrgPlan, server: ServerOrgPlan): OrgPlan => ({
  ...plan,
  ...allowanceFromServer(server.limits, server.entitlements, plan),
});

/** A price as Stripe holds it. */
export type PlanPrice = {
  /** In the currency's smallest unit: 4900 is €49. */
  amount: number;
  /** ISO 4217, as Stripe sends it: "eur". */
  currency: string;
  /** "year", "month". `null` for a one-off price. */
  interval: string | null;
  intervalCount: number | null;
};

/** A plan as the plans page offers it. */
export type PlanOffer = {
  slug: PlanSlug;
  name: string;
  /** `null` for Free, and for a paid plan whose price Stripe hasn't given yet. */
  price: PlanPrice | null;
  /** Pro's trial. `available` is false once the owner has had one. */
  trial: { days: number; available: boolean } | null;
  limits: OrgLimits;
  entitlements: ReadonlySet<OrgEntitlement>;
};

export type PlanCatalog = Readonly<Record<PlanSlug, PlanOffer>>;

const NAMES: Readonly<Record<PlanSlug, string>> = { free: 'Free', essentials: 'Essentials', plus: 'Plus', pro: 'Pro' };

/** The MDC-4517 yearly prices in euro cents, until `GET /plans` answers. */
const TABLE_PRICES: Readonly<Record<PlanSlug, number>> = { free: 0, essentials: 1900, plus: 4900, pro: 9900 };

const tableOffer = (slug: PlanSlug): PlanOffer => ({
  slug,
  name: NAMES[slug],
  price:
    TABLE_PRICES[slug] === 0
      ? null
      : { amount: TABLE_PRICES[slug], currency: 'eur', interval: 'year', intervalCount: 1 },
  trial: null,
  ...planAllowance(slug),
});

/** The plans as the MDC-4517 table has them. What the plans page shows until `GET /plans` answers. */
export const TABLE_PLAN_CATALOG: PlanCatalog = {
  free: tableOffer(PLAN_SLUG.free),
  essentials: tableOffer(PLAN_SLUG.essentials),
  plus: tableOffer(PLAN_SLUG.plus),
  pro: tableOffer(PLAN_SLUG.pro),
};

const isPlanSlug = (slug: string): slug is PlanSlug => (PLANS_IN_ORDER as readonly string[]).includes(slug);

/**
 * The backend's plans over the table. A plan it doesn't list keeps the table's offer, and one this
 * app doesn't know is left out: the page has no column for it.
 */
export const planCatalogFrom = (items: readonly PlanListItem[]): PlanCatalog => {
  const catalog: Record<PlanSlug, PlanOffer> = { ...TABLE_PLAN_CATALOG };

  for (const item of items) {
    if (!isPlanSlug(item.slug)) {
      continue;
    }

    const table = TABLE_PLAN_CATALOG[item.slug];

    catalog[item.slug] = {
      slug: item.slug,
      name: item.name || table.name,
      price: item.price
        ? {
            amount: item.price.amount,
            currency: item.price.currency,
            interval: item.price.interval,
            intervalCount: item.price.interval_count,
          }
        : table.price,
      trial: item.trial,
      ...allowanceFromServer(item.limits, item.entitlements, table),
    };
  }

  return catalog;
};

export const fetchPlanCatalog = async (apiClient: ApiClient): Promise<PlanCatalog> => {
  const { data } = await apiClient.get('/plans');

  return planCatalogFrom(data);
};

/** "€49", "€49.50", or "€0" for no price. */
export const formatPlanPrice = (price: PlanPrice | null): string => {
  if (!price) {
    return '€0';
  }

  const major = price.amount / 100;

  return new Intl.NumberFormat('en', {
    style: 'currency',
    currency: price.currency.toUpperCase(),
    minimumFractionDigits: Number.isInteger(major) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(major);
};

/** `yr`, `mo` — the period in a price's short form. An interval this does not know is shown as sent. */
const shortPeriod = (interval: string): string => {
  switch (interval) {
    case 'year':
      return i18n.t('subscription:period.short.year');
    case 'month':
      return i18n.t('subscription:period.short.month');
    default:
      return interval;
  }
};

/** "€49/yr", or "€0" for no price. The short form, for buttons and column heads. */
export const planPriceShort = (price: PlanPrice | null): string => {
  const amount = formatPlanPrice(price);

  if (!price?.interval || (price.intervalCount ?? 1) !== 1) {
    return amount;
  }

  return `${amount}/${shortPeriod(price.interval)}`;
};

/** "year", "2 years", "month". `null` for no price or a one-off one. */
export const planPricePeriod = (price: PlanPrice | null): string | null => {
  if (!price?.interval) {
    return null;
  }

  const count = price.intervalCount ?? 1;

  switch (price.interval) {
    case 'year':
      return i18n.t('subscription:period.year', { count });
    case 'month':
      return i18n.t('subscription:period.month', { count });
    default:
      return count === 1 ? price.interval : `${count} ${price.interval}s`;
  }
};
